/**
 * The one place that knows how a cell becomes a pixel.
 *
 * The board has no camera. It lays its grid rectangle onto four authored corners by
 * BILINEAR interpolation — the same `applyMorph` every canvas layer uses — which is
 * not a perspective projection: no camera pose reproduces it, and a homography
 * fitted through the corners bows away from the map by most of a cell in the middle
 * of a large board (see services/model3d/gridCamera.js, which hit exactly this and
 * solved it per cell).
 *
 * So the effect layer does not get a camera either. It gets THIS: the four corners
 * of the grid in final canvas pixels, and the same bilinear map, evaluated in the
 * vertex shader. Zoom, pan and the corner morph are all folded into those four
 * points before they are handed over — the canvas transform is affine, so
 * transforming the corners and then interpolating is exactly interpolating and then
 * transforming.
 *
 * The consequence is the property the whole system rests on: the effect layer and
 * the painted board cannot disagree about where a cell is, at any zoom, under any
 * morph, because they are evaluating one function of one set of numbers.
 */

/** Accepts the tactical fight's snake_case grid and the map editor's camelCase one. */
export function readGridConfig(source) {
  if (!source) return null;
  const columns = source.columns ?? source.grid_columns ?? source.gridColumns;
  const rows = source.rows ?? source.grid_rows ?? source.gridRows;
  const cellWidth = source.cellWidth ?? source.cell_width;
  const cellHeight = source.cellHeight ?? source.cell_height;
  if (!columns || !rows || !cellWidth || !cellHeight) return null;
  return {
    columns,
    rows,
    cellWidth,
    cellHeight,
    cornerOffsets: source.cornerOffsets ?? source.corner_offsets ?? null,
    referenceImageWidth: source.referenceImageWidth ?? source.reference_width ?? null,
    referenceImageHeight: source.referenceImageHeight ?? source.reference_height ?? null,
  };
}

/**
 * Rescale a grid authored against one image size onto the image actually loaded.
 * Uploads get resampled; without this the lattice drifts off the drawn walls.
 */
export function scaleGridToImage(config, image) {
  if (!config) return null;
  if (!config.referenceImageWidth || !config.referenceImageHeight || !image) return config;
  return {
    ...config,
    cellWidth: (config.cellWidth * image.width) / config.referenceImageWidth,
    cellHeight: (config.cellHeight * image.height) / config.referenceImageHeight,
  };
}

const ZERO = { x: 0, y: 0 };

export class GridProjection {
  /**
   * @param config    grid config, already scaled to the loaded image
   * @param options   origin   grid top-left in unzoomed canvas pixels
   *                  zoom     the canvas scale
   *                  viewport canvas size in CSS pixels
   */
  constructor(config, { origin = ZERO, zoom = 1, viewport = { width: 0, height: 0 } } = {}) {
    this.config = config;
    this.origin = origin;
    this.zoom = zoom || 1;
    this.viewport = viewport;
  }

  get columns() { return this.config?.columns || 0; }

  get rows() { return this.config?.rows || 0; }

  /** Unzoomed canvas pixels for a point given as a fraction of the grid rectangle. */
  morph(u, v) {
    const { columns, rows, cellWidth, cellHeight, cornerOffsets } = this.config;
    const gridWidth = columns * cellWidth;
    const gridHeight = rows * cellHeight;
    const startX = this.origin.x;
    const startY = this.origin.y;

    const offset = (corner) => ({
      x: (cornerOffsets?.[corner]?.x || 0) * gridWidth,
      y: (cornerOffsets?.[corner]?.y || 0) * gridHeight,
    });

    const tl = offset('topLeft');
    const tr = offset('topRight');
    const bl = offset('bottomLeft');
    const br = offset('bottomRight');

    const topX = (startX + tl.x) * (1 - u) + (startX + gridWidth + tr.x) * u;
    const topY = (startY + tl.y) * (1 - u) + (startY + tr.y) * u;
    const bottomX = (startX + bl.x) * (1 - u) + (startX + gridWidth + br.x) * u;
    const bottomY = (startY + gridHeight + bl.y) * (1 - u) + (startY + gridHeight + br.y) * u;

    return {
      x: topX * (1 - v) + bottomX * v,
      y: topY * (1 - v) + bottomY * v,
    };
  }

  /** The canvas's own zoom-about-centre transform, applied to one point. */
  toCanvas(point) {
    const cx = this.viewport.width / 2;
    const cy = this.viewport.height / 2;
    return {
      x: cx + (point.x - cx) * this.zoom,
      y: cy + (point.y - cy) * this.zoom,
    };
  }

  /** The four grid corners in final canvas pixels — the whole contract with the shader. */
  corners() {
    return {
      topLeft: this.toCanvas(this.morph(0, 0)),
      topRight: this.toCanvas(this.morph(1, 0)),
      bottomRight: this.toCanvas(this.morph(1, 1)),
      bottomLeft: this.toCanvas(this.morph(0, 1)),
    };
  }

  /** Grid space (cells) to final canvas pixels. */
  gridToScreen(x, z) {
    return this.toCanvas(this.morph(x / this.columns, z / this.rows));
  }

  /** On-screen size of one cell, averaged over the board; the unit height is measured in it. */
  cellSizePx() {
    const { topLeft, topRight, bottomLeft } = this.corners();
    return {
      width: Math.hypot(topRight.x - topLeft.x, topRight.y - topLeft.y) / Math.max(1, this.columns),
      height: Math.hypot(bottomLeft.x - topLeft.x, bottomLeft.y - topLeft.y) / Math.max(1, this.rows),
    };
  }

  /**
   * Canvas pixels back to grid space. Newton on the bilinear map: it is not
   * invertible in closed form for an arbitrary quad, but it is well behaved and
   * settles inside a few iterations at sub-pixel error.
   */
  screenToGrid(px, py) {
    let u = 0.5;
    let v = 0.5;
    for (let i = 0; i < 24; i++) {
      const point = this.toCanvas(this.morph(u, v));
      const errorX = point.x - px;
      const errorY = point.y - py;
      if (Math.abs(errorX) < 0.01 && Math.abs(errorY) < 0.01) break;
      const step = 1e-3;
      const du = this.toCanvas(this.morph(u + step, v));
      const dv = this.toCanvas(this.morph(u, v + step));
      const a = (du.x - point.x) / step;
      const b = (dv.x - point.x) / step;
      const c = (du.y - point.y) / step;
      const d = (dv.y - point.y) / step;
      const determinant = a * d - b * c;
      if (!determinant) break;
      u -= (d * errorX - b * errorY) / determinant;
      v -= (-c * errorX + a * errorY) / determinant;
    }
    return { x: u * this.columns, z: v * this.rows };
  }

  cellAt(px, py) {
    const { x, z } = this.screenToGrid(px, py);
    const col = Math.floor(x);
    const row = Math.floor(z);
    if (col < 0 || row < 0 || col >= this.columns || row >= this.rows) return null;
    return { col, row };
  }

  /** The four corners of one cell, in final canvas pixels — what the 2D layers draw with. */
  cellQuad(col, row) {
    return {
      topLeft: this.gridToScreen(col, row),
      topRight: this.gridToScreen(col + 1, row),
      bottomRight: this.gridToScreen(col + 1, row + 1),
      bottomLeft: this.gridToScreen(col, row + 1),
      center: this.gridToScreen(col + 0.5, row + 0.5),
    };
  }
}

export default GridProjection;
